import { useEffect, useState } from 'react';
import { useSubmissionStore } from './stores/submissionStore';
import { loadCatalog } from './lib/catalog';
import { SingleViewLayout } from './features/single-view/SingleViewLayout';
import { useBrand } from './branding/BrandContext';
import { useTranslation } from './lib/i18n';

export default function App() {
  const setCatalog = useSubmissionStore((s) => s.setCatalog);
  const catalog = useSubmissionStore((s) => s.catalog);
  const brand = useBrand();
  const { t } = useTranslation();
  const [catalogError, setCatalogError] = useState<string | null>(null);

  useEffect(() => {
    loadCatalog()
      .then(setCatalog)
      .catch((err) => {
        console.error('Failed to load catalog:', err);
        setCatalogError(err instanceof Error ? err.message : String(err));
      });
  }, [setCatalog]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', brand.colors.primary);
    root.style.setProperty('--brand-primary-hover', brand.colors.primaryHover);
    root.style.setProperty('--brand-navy', brand.colors.navy);
    root.style.setProperty('--brand-navy-dark', brand.colors.navyDark);
    root.style.setProperty('--brand-blue-accent', brand.colors.blueAccent ?? brand.colors.primary);
    root.style.setProperty('--canvas', '#F5F7FA');
    root.style.setProperty('--surface', '#FFFFFF');
    root.style.setProperty('--ink', '#0F1E33');
    root.style.setProperty('--t2', 'rgba(15,30,51,0.66)');
    root.style.setProperty('--t3', 'rgba(15,30,51,0.62)'); // increased from 0.42 for WCAG AA contrast on white
    root.style.setProperty('--line', 'rgba(15,30,51,0.08)');
    root.style.setProperty('--line-strong', 'rgba(15,30,51,0.16)');
    root.style.setProperty('--brand-font-display', `'${brand.fonts.displayName}', sans-serif`);
    root.style.setProperty('--brand-font-body', `'${brand.fonts.bodyName}', sans-serif`);
    root.style.setProperty(
      '--shadow-card',
      '0 1px 2px rgba(15,30,51,0.04), 0 6px 20px rgba(15,30,51,0.06)',
    );
    root.style.setProperty(
      '--shadow-lift',
      '0 2px 4px rgba(15,30,51,0.05), 0 14px 32px rgba(30,58,95,0.14)',
    );
    root.style.setProperty(
      '--shadow-cta',
      '0 1px 2px rgba(15,30,51,0.10), 0 12px 28px rgba(45,127,249,0.28)',
    );
    root.style.setProperty('--ease', 'cubic-bezier(0.16, 1, 0.3, 1)');
    root.style.setProperty('--ease-spring', 'cubic-bezier(0.34, 1.56, 0.64, 1)');

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = brand.fonts.googleFontsUrl;
    document.head.appendChild(link);

    return () => {
      try {
        document.head.removeChild(link);
      } catch {
        /* idempotent */
      }
    };
  }, [brand]);

  if (catalogError) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{ background: 'var(--canvas,#F5F7FA)' }}
      >
        <div className="max-w-md bg-white p-6 rounded-xl shadow-lg border border-red-200">
          <h1 className="text-lg font-bold text-red-700 mb-2">{t('app.catalog_error.title')}</h1>
          <p className="text-sm text-slate-600 mb-3">{t('app.catalog_error.body')}</p>
          <pre className="bg-slate-100 p-2 rounded text-xs overflow-x-auto whitespace-pre-wrap break-words">
            {catalogError}
          </pre>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 w-full bg-blue-500 text-white py-2 rounded-lg font-bold"
          >
            {t('app.catalog_error.reload')}
          </button>
        </div>
      </div>
    );
  }

  if (!catalog) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{ background: 'var(--canvas,#F5F7FA)' }}
      >
        <p className="text-[var(--t2,rgba(15,30,51,0.66))]">{t('app.loading_catalog')}</p>
      </div>
    );
  }

  return <SingleViewLayout />;
}
